import React, { useEffect, useRef, useState } from 'react';
import FormError from '../../../FormError';
import AutoGrowTextarea from './AutoGrowTextarea';
import { apiFetch, errorText } from '../shared/api';
import { formatPhoneAsTyped } from '../add/formatPhoneAsTyped';
import SentRequestsTable from './SentRequestsTable';
import SentRequestsModal from './SentRequestsModal';
import CollapsibleCardHeader from './CollapsibleCardHeader';
import { normalizePhone } from './more/normalizeChip';
import Spinner from '../shared/Spinner';
import AcknowledgeModal from '../shared/AcknowledgeModal';

// Values are translated here, at module load, so the __() call above them
// sees a string literal - the translations:scan-project-and-add-to-db regex
// only matches a literal first argument, not a variable such as MESSAGE_LABELS[type].
const MESSAGE_LABELS = {
    rating: __('Rating Request Message (1thru5)'),
    testimonial: __('Testimonial Request Message'),
};
const BUTTON_LABELS = {
    rating: __('Send Rating Request'),
    testimonial: __('Send Testimonial Request'),
};
// A phone-number format example, not language-dependent - never wrap in __().
const PHONE_PLACEHOLDER = '(555) 123-4567';
const TOGGLE_ORDER = ['rating', 'testimonial'];
// Fields the form renders a FormError for; a 422 key outside this set has
// nowhere on the page to appear, so it goes to the dialog instead.
const RENDERED_ERROR_FIELDS = ['first_name', 'phone', 'message'];

function fieldError(errors, field) {
    return errors && errors[field] && errors[field][0];
}

function templateFor(demo, type) {
    return (demo.request_templates && demo.request_templates[type]) || '';
}

export default function RequestBlock({ demo, endpoints, limits, timezone = 'UTC', onToast, onRequestSent }) {
    const requestTypes = demo.request_types || [];
    const showTestimonial = requestTypes.includes('testimonial');
    const toggleOptions = TOGGLE_ORDER.filter((option) => option === 'rating' || showTestimonial);
    const [type, setType] = useState(requestTypes[0]);
    const [firstName, setFirstName] = useState('');
    const [collapsed, setCollapsed] = useState(false);
    const [phone, setPhone] = useState('');
    const [ratingMessage, setRatingMessage] = useState(templateFor(demo, 'rating'));
    const [testimonialMessage, setTestimonialMessage] = useState(templateFor(demo, 'testimonial'));
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [acknowledge, setAcknowledge] = useState(null);
    const [lastSent, setLastSent] = useState(null);
    const toggleRefs = useRef({});

    const recentRequests = demo.recent_requests || [];
    const messageByType = { rating: ratingMessage, testimonial: testimonialMessage };
    // Require a complete US phone (10 digits), not just a non-empty field, so the
    // button does not enable on the first digit typed.
    const canSend = firstName.trim() !== '' && phone.replace(/\D/g, '').length >= 10;
    const unchangedSinceSend = lastSent !== null
        && lastSent.type === type
        && lastSent.firstName === firstName
        && normalizePhone(lastSent.phone) === normalizePhone(phone)
        && lastSent.message === messageByType[type];
    const sendLocked = !canSend || unchangedSinceSend;

    useEffect(() => {
        if (requestTypes.length && !requestTypes.includes(type)) {
            setType(requestTypes[0]);
        }
        // Only the available-types list drives this reset - the current `type`
        // selection is an input to the check, not a dependency to react to.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [demo.request_types]);

    const handleTypeChange = (nextType) => {
        if (nextType === type) {
            return;
        }
        setType(nextType);
        setErrors({});
    };

    const focusToggle = (nextType) => {
        handleTypeChange(nextType);
        toggleRefs.current[nextType]?.focus();
    };

    const handleToggleKeyDown = (event) => {
        const direction = { ArrowLeft: -1, ArrowUp: -1, ArrowRight: 1, ArrowDown: 1 }[event.key];
        if (!direction || toggleOptions.length <= 1) {
            return;
        }
        event.preventDefault();
        const currentIndex = toggleOptions.indexOf(type);
        const nextIndex = (currentIndex + direction + toggleOptions.length) % toggleOptions.length;
        focusToggle(toggleOptions[nextIndex]);
    };

    const showFailure = (text) => {
        setAcknowledge({ icon: '\u274c', title: __('Request failed'), message: text });
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        setErrors({});

        try {
            const { status, json } = await apiFetch(endpoints.requests, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    request_type: type,
                    first_name: firstName,
                    phone,
                    message: messageByType[type],
                }),
            });

            if (status >= 200 && status < 300) {
                // Keep First Name + Mobile Phone after a send so the rep can fire the
                // other request type (1thru5 / testimonial) without re-typing them.
                setLastSent({ type, firstName, phone, message: messageByType[type] });
                setAcknowledge({
                    icon: '\u2705',
                    title: __('Request sent'),
                    message: __('Request sent to :name at :phone', { ':name': firstName.trim(), ':phone': phone }),
                });
                if (json?.data) {
                    onRequestSent?.(json.data);
                } else {
                    console.error('RequestBlock: send response had no data', json);
                }
            } else if (status === 422) {
                const nextErrors = json?.errors || {};
                setErrors(nextErrors);
                if (Object.keys(nextErrors).length === 0) {
                    showFailure(errorText(json, __('Something went wrong. Please try again.')));
                } else {
                    const unmappedField = Object.keys(nextErrors).find((field) => !RENDERED_ERROR_FIELDS.includes(field));
                    if (unmappedField) {
                        showFailure(nextErrors[unmappedField][0] || __('Something went wrong. Please try again.'));
                    }
                }
            } else {
                showFailure(errorText(json, __('Something went wrong. Please try again.')));
            }
        } catch (error) {
            console.error(error);
            showFailure(__('Something went wrong. Please try again.'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="card demo-edit-form" data-testid="demo-edit-request">
            <CollapsibleCardHeader
                title={__('Send a link to Rate / Leave a Testimonial')}
                collapsed={collapsed}
                onToggle={() => setCollapsed((prev) => !prev)}
                controlsId="demo-edit-request-body"
            />
            <div className={`card-body${collapsed ? ' d-none' : ''}`} id="demo-edit-request-body">
                <p>{__('Choose what you would like to send:')}</p>

                <div className="btn-group mb-2" role="radiogroup" aria-label={__('Send a link to Rate / Leave a Testimonial')}>
                    <button
                        type="button"
                        id="demo-edit-request-type-rating"
                        ref={(el) => { toggleRefs.current.rating = el; }}
                        className={`btn btn-outline-primary${type === 'rating' ? ' active' : ''}`}
                        role="radio"
                        aria-checked={type === 'rating'}
                        tabIndex={type === 'rating' ? 0 : -1}
                        disabled={submitting}
                        onClick={() => handleTypeChange('rating')}
                        onKeyDown={handleToggleKeyDown}
                    >
                        {__('1thru5')}
                    </button>
                    {showTestimonial &&
                        <button
                            type="button"
                            id="demo-edit-request-type-testimonial"
                            ref={(el) => { toggleRefs.current.testimonial = el; }}
                            className={`btn btn-outline-primary${type === 'testimonial' ? ' active' : ''}`}
                            role="radio"
                            aria-checked={type === 'testimonial'}
                            tabIndex={type === 'testimonial' ? 0 : -1}
                            disabled={submitting}
                            onClick={() => handleTypeChange('testimonial')}
                            onKeyDown={handleToggleKeyDown}
                        >
                            {__('Testimonial')}
                        </button>
                    }
                </div>

                <p>
                    <small className="form-text text-muted">
                        <i className="fas fa-info-circle mr-1" aria-hidden="true" />
                        {type === 'rating'
                            ? __('Send a text message requesting a 1-5 star rating.')
                            : __('Send a text message requesting to record a testimonial video.')}
                    </small>
                </p>

                <div className="row">
                    <div className="form-group col-md-6">
                        <label htmlFor="demo-edit-request-first-name">{__('First Name')}</label>
                        <input
                            id="demo-edit-request-first-name"
                            type="text"
                            className="form-control"
                            style={{ maxWidth: 280 }}
                            placeholder={__('Enter first name')}
                            value={firstName}
                            onChange={(event) => setFirstName(event.target.value)}
                        />
                        <FormError error_message={fieldError(errors, 'first_name')} />
                    </div>
                </div>

                <div className="row">
                    <div className="form-group col-md-6">
                        <label htmlFor="demo-edit-request-phone">{__('Mobile Phone Number')}</label>
                        <input
                            id="demo-edit-request-phone"
                            type="tel"
                            inputMode="tel"
                            className="form-control"
                            style={{ maxWidth: 280 }}
                            placeholder={PHONE_PLACEHOLDER}
                            value={phone}
                            onChange={(event) => setPhone(formatPhoneAsTyped(event.target.value, phone))}
                        />
                        <FormError error_message={fieldError(errors, 'phone')} />
                    </div>
                </div>

                <div className="row">
                    {type === 'rating' ?
                        <div className="form-group col-md-6">
                            <label htmlFor="demo-edit-request-message-rating">{MESSAGE_LABELS.rating}</label>
                            <AutoGrowTextarea
                                id="demo-edit-request-message-rating"
                                className="form-control"
                                maxLength={limits.message}
                                value={ratingMessage}
                                onChange={(event) => setRatingMessage(event.target.value)}
                            />
                            <small className="form-text text-muted text-right d-block">{`${ratingMessage.length}/${limits.message}`}</small>
                            <FormError error_message={fieldError(errors, 'message')} />
                        </div>
                        :
                        <div className="form-group col-md-6">
                            <label htmlFor="demo-edit-request-message-testimonial">{MESSAGE_LABELS.testimonial}</label>
                            <AutoGrowTextarea
                                id="demo-edit-request-message-testimonial"
                                className="form-control"
                                maxLength={limits.message}
                                value={testimonialMessage}
                                onChange={(event) => setTestimonialMessage(event.target.value)}
                            />
                            <small className="form-text text-muted text-right d-block">{`${testimonialMessage.length}/${limits.message}`}</small>
                            <FormError error_message={fieldError(errors, 'message')} />
                        </div>
                    }
                </div>

                <span className={`demo-island-btn-guard${sendLocked ? ' is-disabled' : ''}`}>
                    <button
                        type="button"
                        className="btn btn-primary"
                        disabled={submitting || sendLocked}
                        onClick={handleSubmit}
                    >
                        {submitting
                            ? <Spinner label={__('Sending request...')} className="mr-2" />
                            : <i className="fas fa-paper-plane mr-2" aria-hidden="true" />}
                        {BUTTON_LABELS[type]}
                    </button>
                </span>

                <h3 className="h6 mt-4">{__('Sent requests')}</h3>
                {recentRequests.length > 0
                    ? <SentRequestsTable requests={recentRequests} timezone={timezone} />
                    : <p className="text-muted">{__('No requests sent yet.')}</p>
                }
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setHistoryOpen(true)}>
                    {__('See sent history')}
                </button>
            </div>
            {historyOpen &&
                <SentRequestsModal
                    endpoint={endpoints.requestsList}
                    timezone={timezone}
                    onClose={() => setHistoryOpen(false)}
                    onToast={onToast}
                />
            }
            {acknowledge &&
                <AcknowledgeModal
                    icon={acknowledge.icon}
                    title={acknowledge.title}
                    message={acknowledge.message}
                    onConfirm={() => setAcknowledge(null)}
                />
            }
        </div>
    );
}
